import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * @typedef {Object} DropdownOption
 * @property {string} value - The option value
 * @property {string} label - The display text for the option
 */

/**
 * @typedef {Object} DropdownProps
 * @property {string} value - Current selected value
 * @property {DropdownOption[]} options - Array of options to display
 * @property {(event: { target: { value: string } }) => void} onChange - Function to call when selection changes
 * @property {string} [label] - Label text for the dropdown
 * @property {string} [minWidth] - Minimum width of the dropdown (e.g., '150px', '200px')
 */

/**
 * A custom dropdown component that follows DaisyUI styling
 * @param {DropdownProps} props - The component props
 * @returns {JSX.Element} The rendered dropdown element
 */
export default function Dropdown(props) {
  const { value, options, onChange, label, minWidth = '150px' } = props;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange({ target: { value: option.value } });
    setIsOpen(false);
  };

  return (
    <div className="flex w-full sm:w-fit sm:inline-flex flex-col" ref={dropdownRef}>
      {label && (
        <label className="label py-0.5">
          <span className="label-text text-xs text-gray-600 font-[600]">
            {label}
          </span>
        </label>
      )}
      <div className="relative w-full sm:w-fit">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`h-9 w-full sm:w-fit px-3 py-5 flex items-center justify-between text-sm
            bg-base-100 rounded-btn border border-gray-400
            hover:border-base-content/40 focus:outline-none focus:border-base-content/40
            transition-colors duration-200 cursor-pointer`}
          style={{ minWidth }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span
            className={`truncate ${!selectedOption ? 'text-base-content/50' : ''}`}
          >
            {selectedOption ? selectedOption.label : label}
          </span>
          <ChevronDownIcon
            className={`h-3.5 w-3.5 ml-2 text-gray-400 transition-transform duration-200
            ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute z-50 w-full mt-1.5
            bg-base-100 rounded-btn shadow-lg
            border border-base-content/20
            max-h-60 overflow-auto slim-scrollbar text-sm"
          >
            <div className="py-0.5">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full px-3 py-2 text-left
                    hover:bg-base-200 focus:bg-base-200
                    focus:outline-none transition-colors duration-200
                    ${option.value === value ? 'bg-base-200 font-medium' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
